import React, { useCallback, useEffect, useMemo, useState } from "react";
import Accordion from 'react-bootstrap/Accordion';
import Spinner from 'react-bootstrap/Spinner';
import { Button, Form, InputGroup, Nav } from "react-bootstrap";
import ProductSewingStatusModal from "../components/ProductSewingStatusModal";
import ProductCardModal from "../components/ProductCardModal";
import StockBagsPanel from "../components/StockBagsPanel";
import { isManagerRole } from "../helpers/roles";
import {compareOfferWithCell} from '../helpers/utils';
import { sorStartEnd } from '../helpers/utils';
import { sorEndStart } from '../helpers/utils';
import ProgressBar from "../components/ProgressBar/ProgressBar";
import io from "socket.io-client";
const SERVER_URL = process.env.NODE_ENV === "production"
    ? "https://aqueous-atoll-11213.herokuapp.com"
    : "http://localhost:5001";

/** Значення з CRM (пробіли, регістр) */
function trimStockPropValue(value) {
    if (value == null) return "";
    return String(value).trim();
}

/** «Молочний · L», «Синій · XS» — як у прайсі KeyCRM */
function splitColorSizeFromVariantValue(value) {
    const v = trimStockPropValue(value);
    const m = v.match(/^(.+?)\s*[·⋅•]\s*(.+)$/u);
    if (!m) return null;
    return { color: m[1].trim(), size: m[2].trim() };
}

/**
 * Ім'я властивості з CRM → роль. NFKC + типові опечатки (латинська i в «Розмiр»).
 */
function stockPropertyRole(name) {
    const raw = String(name || "").trim();
    if (!raw) return null;
    const nk = raw.toLowerCase().normalize("NFKC");
    const nkFix = nk.replace(/озмiр/g, "озмір");

    if (["тип", "модель"].includes(nkFix) || nk.includes("модель") || nk.includes("тип")) return "model";
    if (
        ["мова", "колір", "color", "colour", "koлір"].includes(nkFix) ||
        nk.includes("колір") ||
        nk.includes("kolir")
    )
        return "color";
    if (
        ["розмір", "size", "размер"].includes(nkFix) ||
        nkFix.includes("розмір") ||
        nkFix.includes("размер") ||
        /\bsize\b/.test(nk)
    )
        return "size";
    if (["варіант", "variant"].includes(nkFix)) return "variant";
    return null;
}

const STOCK_SIZE_VALUE_RE = /^(XXS|XS|S|M|L|XL|XXL|XXXL)$/i;

function applyStocksOfferPropsAndCollectAxes(offers) {
    const colors = new Set();
    const sizes = new Set();
    const models = new Set();

    offers.forEach((offer) => {
        const obj = { model: "", color: "", size: "" };

        (offer.properties || []).forEach(({ name, value }) => {
            const v = trimStockPropValue(value);
            const role = stockPropertyRole(name);
            if (role === "variant") {
                const sp = splitColorSizeFromVariantValue(v);
                if (sp) {
                    obj.color = sp.color;
                    obj.size = sp.size;
                }
                return;
            }
            if (!role) return;
            if (role === "color") {
                const sp = splitColorSizeFromVariantValue(v);
                if (sp) {
                    obj.color = sp.color;
                    obj.size = obj.size || sp.size;
                } else {
                    obj.color = v;
                }
                return;
            }
            if (role === "size") {
                obj.size = v;
                return;
            }
            obj[role] = v;
        });

        if (!obj.size && obj.color) {
            const sp = splitColorSizeFromVariantValue(obj.color);
            if (sp) {
                obj.color = sp.color;
                obj.size = sp.size;
            }
        }

        if (!obj.size && (offer.properties || []).length) {
            for (const { name, value } of offer.properties) {
                const v = trimStockPropValue(value);
                if (!v || stockPropertyRole(name)) continue;
                if (STOCK_SIZE_VALUE_RE.test(v)) {
                    obj.size = v;
                    break;
                }
            }
        }

        if (obj.model) models.add(obj.model);
        if (obj.color) colors.add(obj.color);
        if (obj.size) sizes.add(obj.size);

        const { model, color, size } = obj;
        offer.props = model ? [model, color, size] : [color, size];
    });

    return { colors, sizes, models };
}

function getStocksCellQty(offers, model, color, size) {
    let quantity = "";
    offers.forEach(offer => {
        if (model) {
            if (compareOfferWithCell(offer.props, [model, color, size])) {
                const q = offer.quantity - (offer.in_reserve || 0);
                quantity = q !== 0 ? q : "";
            }
        } else if (compareOfferWithCell(offer.props, [color, size])) {
            const q = offer.quantity - (offer.in_reserve || 0);
            quantity = q !== 0 ? q : "";
        }
    });
    return quantity;
}

function modelRowSpanForRow(rows, startIdx) {
    const m = rows[startIdx].model;
    let n = 0;
    for (let j = startIdx; j < rows.length && rows[j].model === m; j++) n++;
    return n;
}

const STOCK_WAREHOUSE_STORAGE_KEY = "lavaStockWarehouseId";
const ALLOWED_STOCK_WAREHOUSE_IDS = [1, 3, 4];

function readSavedStockWarehouseId() {
    if (typeof window === "undefined") return 1;
    const n = Number(window.localStorage.getItem(STOCK_WAREHOUSE_STORAGE_KEY));
    return ALLOWED_STOCK_WAREHOUSE_IDS.includes(n) ? n : 1;
}

function buildVisibleStockTableRows(models, colors, sizes, offers) {
    const rows = [];
    if (models.length) {
        models.forEach(m => {
            colors.forEach(c => {
                if (sizes.some(s => getStocksCellQty(offers, m, c, s) !== "")) {
                    rows.push({ model: m, color: c });
                }
            });
        });
    } else {
        colors.forEach(c => {
            if (sizes.some(s => getStocksCellQty(offers, undefined, c, s) !== "")) {
                rows.push({ color: c });
            }
        });
    }
    const visibleSizes = sizes.filter(s =>
        rows.some(r => getStocksCellQty(offers, r.model, r.color, s) !== "")
    );
    /* Плюс у дужках; negativeSum — сума від’ємних комірок (≤ 0), для підпису в заголовку */
    let displayTotal = 0;
    let negativeSum = 0;
    rows.forEach(r => {
        visibleSizes.forEach(s => {
            const q = getStocksCellQty(offers, r.model, r.color, s);
            if (q !== "" && q != null) {
                const n = Number(q);
                if (n > 0) displayTotal += n;
                else if (n < 0) negativeSum += n;
            }
        });
    });
    return { rows, visibleSizes, displayTotal, negativeSum };
}

/** Сума від’ємних залишків по товарах (рівень SKU/складу), ≤ 0 */
function sumWarehouseNegativeQuantities(filteredProducts) {
    return filteredProducts.reduce((sum, p) => {
        const q = p.quantity;
        return sum + (typeof q === "number" && q < 0 ? q : 0);
    }, 0);
}

/** Крон синхронізації складу (cron/warehouseFullSync.job.js): щогодини 09:00–23:00 за Києвом. */
const SYNC_CRON_FIRST_HOUR = 9;
const SYNC_CRON_LAST_HOUR = 23;
/** Крон раз на годину — якщо даним більше 90 хв у робочі години, синхронізація, схоже, не працює. */
const SYNC_STALE_MINUTES = 90;

function kyivHourNow() {
    return Number(
        new Intl.DateTimeFormat("en-GB", {
            hour: "2-digit",
            hour12: false,
            timeZone: "Europe/Kyiv",
        }).format(new Date())
    );
}

/** Підпис «дд.мм.рррр, гг:хв (N хв тому)» + ознака застарілості для часу останнього оновлення складу. */
function describeStockLastUpdate(iso) {
    const date = new Date(iso);
    if (!iso || Number.isNaN(date.getTime())) return null;

    const ageMin = Math.max(0, Math.round((Date.now() - date.getTime()) / 60000));
    let ago;
    if (ageMin < 1) ago = "щойно";
    else if (ageMin < 60) ago = `${ageMin} хв тому`;
    else if (ageMin < 48 * 60) ago = `${Math.floor(ageMin / 60)} год ${ageMin % 60} хв тому`;
    else ago = `${Math.floor(ageMin / 1440)} дн тому`;

    const hour = kyivHourNow();
    const cronShouldBeRunning = hour >= SYNC_CRON_FIRST_HOUR + 1 && hour <= SYNC_CRON_LAST_HOUR;

    return {
        text: `${date.toLocaleString("uk-UA", {
            timeZone: "Europe/Kyiv",
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        })} (${ago})`,
        stale: cronShouldBeRunning && ageMin > SYNC_STALE_MINUTES,
    };
}

/**
 * Видимість SKU і число «Зал» у поточному фільтрі складу — та сама логіка, що в fetchData (/api/stocks).
 * @returns {number|null} null — SKU не показують у цій вкладці складу
 */
function getStockQuantityForWarehouseFilter(product, selectedWarehouseId) {
    if (selectedWarehouseId === 4) {
        const validWarehouses = product.warehouse?.filter(wh => wh.id === 1 || wh.id === 3);
        if (!validWarehouses?.length) return null;
        return validWarehouses.reduce((sum, wh) => sum + wh.quantity - wh.reserve, 0);
    }
    const warehouseEntry = product.warehouse?.find(wh => wh.id === selectedWarehouseId);
    if (!warehouseEntry) return null;
    return warehouseEntry.quantity - warehouseEntry.reserve;
}

/**
 * Проблемні позиції з локального дзеркала складу (ProductW, відповідь `/api/stocks`).
 * Не викликає KeyCRM `/api/products` — на проді він часто дає 503/таймаут.
 * Логіка як раніше для CRM: порожній SKU або закупівельна ціна 0 / відсутня.
 */
function deriveProblemProductsFromMirror(rows, selectedWarehouseId) {
    if (!Array.isArray(rows) || rows.length === 0) return [];

    const out = [];
    const seen = new Set();

    for (const raw of rows) {
        const rowId = raw?.id;
        if (rowId != null) {
            if (seen.has(rowId)) continue;
            seen.add(rowId);
        }

        const qty = getStockQuantityForWarehouseFilter(raw, selectedWarehouseId);
        if (qty === null) continue;

        const hasEmptySku = raw.sku == null || String(raw.sku).trim() === "";

        const priceNum = Number(raw.purchased_price);
        const hasEmptyPrice =
            raw.purchased_price == null ||
            raw.purchased_price === "" ||
            !Number.isFinite(priceNum) ||
            priceNum === 0;

        if (!hasEmptySku && !hasEmptyPrice) continue;

        out.push({
            product: { name: raw.name ?? "—" },
            product_id: raw.product_id,
            quantity: qty,
            sku: raw.sku,
            purchased_price: raw.purchased_price,
        });
    }

    out.sort((a, b) =>
        String(a.product.name).localeCompare(String(b.product.name), "uk")
    );
    return out;
}

/**
 * Підпис кнопки пошиву на /stocks: скільки SKU товару в SewingExclusion (допродаж = «не шиємо»).
 * Після «Зберегти» в модалці: усі в пошиві → «Шиємо»; змішано → «Частково»; усі в допродажі → «Не шиємо».
 * @param {"idle"|"loading"|"ok"|"error"} exclusionsStatus
 */
function productSewingStatusButtonMeta(productId, allProducts, excludedSkuSet, exclusionsStatus) {
    const pid = Number(productId);
    if (!Number.isFinite(pid) || pid <= 0) {
        return {
            label: "—",
            variant: "outline-secondary",
            title: "",
            disabled: true,
        };
    }
    const skus = new Set();
    for (const row of allProducts || []) {
        if (Number(row.product_id) !== pid) {
            continue;
        }
        const s = String(row.sku || "").trim().normalize("NFKC");
        if (s) {
            skus.add(s);
        }
    }
    const list = [...skus];
    if (!list.length) {
        return {
            label: "Без SKU",
            variant: "outline-secondary",
            title: "Немає SKU для цього товару в локальній базі",
            disabled: false,
        };
    }
    if (exclusionsStatus === "idle" || exclusionsStatus === "loading") {
        return {
            label: "…",
            variant: "outline-secondary",
            title: "Завантаження статусу пошиву…",
            disabled: true,
        };
    }
    if (exclusionsStatus === "error") {
        return {
            label: "Пошив: ?",
            variant: "outline-secondary",
            title: "Не вдалося завантажити список виключень з пошиву. Натисніть, щоб змінити SKU вручну.",
            disabled: false,
        };
    }
    let excluded = 0;
    for (const s of list) {
        if (excludedSkuSet.has(s.normalize("NFKC"))) {
            excluded += 1;
        }
    }
    const total = list.length;
    if (excluded === 0) {
        return {
            label: "Шиємо",
            variant: "outline-success",
            title: `Усі ${total} SKU у пошиві (жоден не в допродажі). Як у модалці — усі галочки увімкнені. Натисніть, щоб змінити.`,
            disabled: false,
        };
    }
    if (excluded === total) {
        return {
            label: "Не шиємо",
            variant: "outline-warning",
            title: `Усі ${total} SKU не в пошиві (усі в допродажі). Як у модалці — усі галочки зняті. Натисніть, щоб змінити.`,
            disabled: false,
        };
    }
    return {
        label: "Частково",
        variant: "outline-secondary",
        title: `${excluded} з ${total} SKU в допродажі, решта — у пошиві (не всі галочки однаково). Натисніть для деталей.`,
        disabled: false,
    };
}

export const Stocks = ({ user }) => {
    const [totalStock, setTotalStock] = useState(0);
    const [warehouseNegativeSum, setWarehouseNegativeSum] = useState(0);
    const [loadingDB, setLoadingDB] = useState(false);
    const [errorDB, setErrorDB] = useState(null);
    const [lastUpdate, setLastUpdate] = useState(null);
    const [negativeStockItems, setNegativeStockItems] = useState([]);
    const [selectedWarehouseId, setSelectedWarehouseId] = useState(readSavedStockWarehouseId);
    const [problemProducts, setProblemProducts] = useState([]);
    const [showProblems, setShowProblems] = useState(false);
    const [showBags, setShowBags] = useState(false);
    /** Мішки приховані для менеджерів Директу (група в графіку змін) — вирішує сервер. */
    const [canSeeBags, setCanSeeBags] = useState(false);

    useEffect(() => {
        let cancelled = false;
        fetch("/api/stock-bags/access", { credentials: "include", cache: "no-store" })
            .then((res) => (res.ok ? res.json() : { allowed: false }))
            .then((data) => {
                if (!cancelled) setCanSeeBags(Boolean(data?.allowed));
            })
            .catch(() => {
                if (!cancelled) setCanSeeBags(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);
    const [problemsFetchError, setProblemsFetchError] = useState(null);
    const [sewingModal, setSewingModal] = useState({
        open: false,
        productId: null,
        name: "",
    });
    const [productCardModal, setProductCardModal] = useState({
        open: false,
        mode: "view",
        productId: null,
        name: "",
    });

    const [excludedSkuSet, setExcludedSkuSet] = useState(() => new Set());
    /** idle — кнопки немає; loading / ok / error — для підпису кнопки */
    const [sewingExclusionsStatus, setSewingExclusionsStatus] = useState("idle");

    const [state, setState] = useState({
        warehouses: [],
        groupedProducts: [],
        products: [],
        loading: true,
    });

    const { warehouses, groupedProducts, products, loading } = state;

    /** Вкладки складу + IG-фільтр — лише для менеджерів; інші завжди бачать «Всі». */
    const showManagerStockTabs = isManagerRole(user?.role);
    const [stockTab, setStockTab] = useState(() =>
        isManagerRole(user?.role) ? "main" : "all"
    );
    const [igSoldProductIdList, setIgSoldProductIdList] = useState([]);
    const [igSalesLoaded, setIgSalesLoaded] = useState(false);
    const [igSalesError, setIgSalesError] = useState(false);
    const igSoldProductSet = useMemo(() => new Set(igSoldProductIdList), [igSoldProductIdList]);
    const igFilterActive = showManagerStockTabs && igSalesLoaded && !igSalesError;
    const effectiveStockTab = showManagerStockTabs ? stockTab : "all";

    const isAD = user?.role === "admin" || user?.role === "director";

    const canOpenSewingStatusModal =
        user?.role === "admin" ||
        user?.role === "director" ||
        user?.role === "finance";

    const loadSewingExclusions = useCallback(async () => {
        setSewingExclusionsStatus("loading");
        try {
            const res = await fetch(
                `/api/process-plans/sewing-exclusions?_=${Date.now()}`,
                {
                    credentials: "include",
                    cache: "no-store",
                }
            );
            if (!res.ok) {
                throw new Error("sewing-exclusions failed");
            }
            const data = await res.json();
            const next = new Set(
                (Array.isArray(data) ? data : [])
                    .map((x) => String(x.sku || "").trim().normalize("NFKC"))
                    .filter(Boolean)
            );
            setExcludedSkuSet(next);
            setSewingExclusionsStatus("ok");
        } catch (e) {
            console.error(e);
            setExcludedSkuSet(new Set());
            setSewingExclusionsStatus("error");
        }
    }, []);

    const loadLastUpdate = useCallback(async () => {
        try {
            const res = await fetch(`/api/last-update?_=${Date.now()}`, {
                credentials: "include",
                cache: "no-store",
            });
            if (!res.ok) throw new Error("last-update failed");
            const data = await res.json();
            setLastUpdate(data.lastUpdated || null);
        } catch (e) {
            console.error(e);
        }
    }, []);

    useEffect(() => {
        if (!canOpenSewingStatusModal) {
            setSewingExclusionsStatus("idle");
            setExcludedSkuSet(new Set());
            return undefined;
        }
        void loadSewingExclusions();
        return undefined;
    }, [canOpenSewingStatusModal, loadSewingExclusions]);

    useEffect(() => {
        if (!isManagerRole(user?.role)) {
            setStockTab("all");
            setIgSoldProductIdList([]);
            setIgSalesLoaded(false);
            setIgSalesError(false);
            return undefined;
        }

        setStockTab("main");

        let cancelled = false;
        setIgSalesLoaded(false);
        setIgSalesError(false);
        (async () => {
            try {
                const res = await fetch("/api/stocks/instagram-sold-product-ids?days=30", {
                    credentials: "include",
                    cache: "no-store",
                });
                if (!res.ok) throw new Error("ig product ids");
                const data = await res.json();
                const list = Array.isArray(data.productIds) ? data.productIds : [];
                if (!cancelled) {
                    setIgSoldProductIdList(list.map((x) => Number(x)).filter((n) => Number.isFinite(n)));
                }
            } catch (e) {
                console.error(e);
                if (!cancelled) {
                    setIgSoldProductIdList([]);
                    setIgSalesError(true);
                }
            } finally {
                if (!cancelled) setIgSalesLoaded(true);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [user?.role]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setState(prevState => ({ ...prevState, loading: true }));
                void loadLastUpdate();

                const response = await fetch("/api/stocks", {
                    credentials: "include"
                });
                if (!response.ok) throw new Error("Помилка завантаження даних");

                const data = await response.json();
                let newNegativeStockItems = [];

                let filteredProducts;

                if (selectedWarehouseId === 4) {
                    filteredProducts = data
                        .map(product => {
                            const validWarehouses = product.warehouse?.filter(wh => wh.id === 1 || wh.id === 3);
                            if (!validWarehouses?.length) return null;

                            const totalQuantity = validWarehouses.reduce((sum, wh) => sum + wh.quantity - wh.reserve, 0);

                            if (totalQuantity < 0) {
                                const props = product.properties.map(obj => obj.value).join(" ");
                                newNegativeStockItems.push({ name: product.name, props, quantity: totalQuantity });
                            }

                            return {
                                ...product,
                                quantity: totalQuantity,
                                warehouse: validWarehouses,
                            };
                        })
                        .filter(Boolean);
                } else {
                    filteredProducts = data
                        .map(product => {
                            const warehouseEntry = product.warehouse?.find(wh => wh.id === selectedWarehouseId);
                            if (!warehouseEntry) return null;

                            const quantity = warehouseEntry.quantity - warehouseEntry.reserve;
                            if (quantity < 0) {
                                const props = product.properties.map(obj => obj.value).join(" ");
                                newNegativeStockItems.push({ name: product.name, props, quantity });
                            }

                            return {
                                ...product,
                                quantity,
                            };
                        })
                        .filter(Boolean);
                }

                newNegativeStockItems.sort((a, b) => a.quantity - b.quantity);
                setNegativeStockItems(newNegativeStockItems);

                const grouped = filteredProducts.reduce((acc, item) => {
                    const idx = acc.findIndex(group => group[0]?.product_id === item.product_id);
                    if (idx !== -1) acc[idx].push(item);
                    else acc.push([item]);
                    return acc;
                }, []);

                grouped.sort((a, b) => a[0]?.name?.localeCompare(b[0]?.name, 'uk'));

                const total = filteredProducts.reduce((sum, p) => sum + (p.quantity || 0), 0);
                setTotalStock(total);
                setWarehouseNegativeSum(sumWarehouseNegativeQuantities(filteredProducts));

                setState({
                    warehouses: [], // если не используешь — можно убрать
                    groupedProducts: grouped,
                    products: data,
                    loading: false,
                });
            } catch (error) {
                console.error("Request failed", error);
                setState(prev => ({ ...prev, loading: false }));
            }
        };

        fetchData();
    }, [selectedWarehouseId, loadLastUpdate]);

    useEffect(() => {
        if (!showProblems) return;
        try {
            setProblemsFetchError(null);
            setProblemProducts(deriveProblemProductsFromMirror(products, selectedWarehouseId));
        } catch (err) {
            console.error(err);
            setProblemProducts([]);
            setProblemsFetchError(
                "Не вдалося сформувати список з даних складу. Спробуйте оновити сторінку."
            );
        }
    }, [showProblems, products, selectedWarehouseId]);

    const handleToggleProblemProducts = () => {
        if (showProblems) {
            setShowProblems(false);
            setProblemsFetchError(null);
            return;
        }

        setShowProblems(true);
        setProblemsFetchError(null);

        try {
            setProblemProducts(deriveProblemProductsFromMirror(products, selectedWarehouseId));
        } catch (err) {
            console.error(err);
            setProblemProducts([]);
            setProblemsFetchError(
                "Не вдалося сформувати список з даних складу. Спробуйте оновити сторінку."
            );
        }
    };

    if (loading) {
        return <div>Завантаження…</div>;
    }

    let SOURCE_MAP  = [
        { id: 1, name: "Харків" },
        // { id: 2, name: "Брак" },
        { id: 3, name: "Київ" },
        { id: 4, name: "Усього" },
    ];

    const warehouseMap = {
        1: "Харків",
        // 2: "Брак",
        3: "Київ",
        4: "Усього"
    };

    function handleSelectChange(event) {
        const selectedId = parseInt(event.target.value);
        setSelectedWarehouseId(selectedId);
        try {
            window.localStorage.setItem(STOCK_WAREHOUSE_STORAGE_KEY, String(selectedId));
        } catch {
            /* ignore */
        }

        let filteredProducts = [];
        let newNegativeStockItems = [];

        if (selectedId === 4) {
            filteredProducts = products.map(product => {
                const validWarehouses = product.warehouse?.filter(wh => wh.id === 1 || wh.id === 3);
                const totalQuantity = validWarehouses.reduce((sum, wh) => sum + wh.quantity - wh.reserve, 0);

                if (totalQuantity < 0) {
                    const props = product.properties.map(obj => obj.value).join(" ");
                    newNegativeStockItems.push({name: product.name, props, quantity: totalQuantity});
                }
                return {
                    ...product,
                    quantity: totalQuantity !== 0 ? totalQuantity : "",
                    warehouse: validWarehouses,
                };
            });
        } else {
            filteredProducts = products
                .map(product => {
                    const warehouseEntry = product.warehouse?.find(wh => wh.id === selectedId);
                    if (!warehouseEntry) return null;

                    const quantity = warehouseEntry.quantity - warehouseEntry.reserve;
                    if (quantity < 0) {
                        const props = product.properties.map(obj => obj.value).join(" ");
                        newNegativeStockItems.push({name: product.name, props, quantity});
                    }

                    return {
                        ...product,
                        quantity: quantity
                    };
                })
                .filter(Boolean);
        }

        newNegativeStockItems.sort((a, b) => a.quantity - b.quantity);

        setNegativeStockItems(newNegativeStockItems)

        filteredProducts.sort((a, b) => a.name.localeCompare(b.name, 'uk'));


        let prods = filteredProducts.reduce((a, c) => {
            let idx = a.findIndex((e) => e[0]?.product_id === c.product_id);
            if (idx !== -1) a[idx].push(c);
            else a.push([c]);
            return a;
        }, []);

        setState(prevState => ({
            ...prevState,
            groupedProducts: prods.length ? prods : prevState.groupedProducts,
        }));



        // Общее количество товара для выбранного склада
        const totalStock = filteredProducts.reduce((sum, p) => sum + (p.quantity || 0), 0);
        setTotalStock(totalStock);
        setWarehouseNegativeSum(sumWarehouseNegativeQuantities(filteredProducts));
    }


    // const namesAllProducts = []
        // const namesAllProducts = ["Піжама з кантом", "Світшот Paris", "Піжама з воланами", "Кофта з блискавкою", "Труси з рюшами", "Світшот California", "Худі Yale", "Світшот Harvard", "Світшот Yale", 'Блузка муслін', 'Джогери Фліс', 'Джогери двонить', 'Джогери петля 2022', 'Джогери петля 2023', 'Джогери чоловічі', 'Джогери чоловічі 2023', 'Зіпхуді Фліс', 'Зіпхуді петля 2022', 'Зіпхуді петля 2023', 'Карго', 'Карго 2023', 'Комплект білизни', 'Комплект білизни new', 'Комплект білизни new з чашечками', 'Комплект білизни з чашечками', 'Комплект з велосипедками', 'Костюм прошва', 'Купальник бразиліани', 'Купальник квадрат', "Купальник на зав'язках", 'Купальник трикутник', 'Куртка', 'Куртка 2022', 'Лонгслів 2023', 'Лонгслів двонить', 'Лонгслів з віскози', 'Лонгслів на затяжках', 'Лосини', 'Майка', "Майка з зав'язками", 'Майка з кантом', 'Мастерка', 'Прямі штани', 'Рашгард', 'Рукавиці', 'Сарафан льон', 'Світшот Фліс', 'Світшот петля 2021', 'Світшот петля 2022', 'Світшот петля 2023', 'Сорочка джинс', 'Сорочка льон', 'Сорочка льон 2023', 'Сорочка тепла', 'Спідниця джинс', 'Спідниця муслін', 'Сукня', 'Сукня 2023', 'Сукня довга', 'Топ', 'Топ new', 'Топ з чашечками', 'Топ трикутник льон', 'Топ трикутник муслін', "Топ із зав'язками", 'Труси', 'Труси new', 'Туніка на затяжках', 'Футболка Двонитка', 'Футболка Петля', 'Футболка чоловіча', 'Худі Тедді', 'Худі Фліс', 'Худі петля 2021', 'Худі петля 2022', 'Худі петля 2023', 'Худі чоловічі Фліс', 'Шапка', 'Шерпа', 'Шорти двонить', 'Шорти канада', 'Шорти короткі', 'Шорти льон', 'Шорти льон 2023', 'Шорти петля', 'Штани льон', 'Штани льон 2023', 'Штани муслін']
        const namesAllProducts = ['Куртка 2022', 'Лосини', 'Сукня', 'Куртка', 'Сукня довга', 'Топ', 'Топ new', 'Топ з чашечками', 'Труси', 'Труси new', 'Худі Тедді', 'Худі петля 2021', 'Худі чоловічі Фліс']


        const sendToTelegram = async () => {
            const botTOKEN = "bot5346603542:AAFy1TKplf7w8EG2rdVigK-DvXTTjEjWDtg";
            const chat_id = "-715587093";
            // const chat_id = "@lava_expenses"; // LAVA test
            const warehouseName = warehouseMap[selectedWarehouseId] || "Невідомий склад";
            const negativeTotal = negativeStockItems.reduce((sum, item) => sum + item.quantity, 0);
            const message = `<b>${warehouseName}</b> \n\n` + `<b>Речі продані у мінус (разом: ${negativeTotal} шт.):</b> \n\n` + negativeStockItems.map(item => `${item.name} (${item.props}): ${item.quantity}`).join('\n');
            await fetch(`https://api.telegram.org/${botTOKEN}/sendMessage?chat_id=${chat_id}&parse_mode=html&text=${encodeURI(message)}`);
        }
        const sendMessage = async (e) => {
            e.preventDefault()
            console.log("negativeStockItems3:", negativeStockItems);
            await sendToTelegram()
        }

    const handleRefresh = async () => {
        setLoadingDB(true);
        setErrorDB(null);

        try {
            const response = await fetch('/api/stocks_to_database', {
                method: 'GET',
                credentials: 'include'
            });

            if (!response.ok) {
                throw new Error("Помилка під час оновлення складів");
            }

            // Тут можно обновить состояние или вызвать функцию для обновления UI
            await loadLastUpdate();
            alert("Склади успішно оновлено!");
        } catch (err) {
            console.error(err);
            setErrorDB(err.message || "Щось пішло не так");
        } finally {
            setLoadingDB(false);
        }
    };

    const renderFilteredStockRows = (rows, visibleSizes, offers, hasModels) => {
        return rows.map((row, idx) => {
            const showModel = hasModels && (idx === 0 || rows[idx - 1].model !== row.model);
            return (
                <tr key={`${row.model}-${row.color}-${idx}`}>
                    {hasModels && showModel ? (
                        <td rowSpan={modelRowSpanForRow(rows, idx)}>{row.model}</td>
                    ) : null}
                    <td style={{ textAlign: "left" }}>{row.color}</td>
                    {visibleSizes.map((s, si) => {
                        const quantity = getStocksCellQty(offers, row.model, row.color, s);
                        return <td key={si} className={quantity ? "notEmpty" : "empty"}>{quantity}</td>;
                    })}
                </tr>
            );
        });
    };
    const renderAccordionItems = () => {
        return groupedProducts.length ? (
            groupedProducts
                .map((product, p) => {
                    const offers = product;
                    const offerFirst = offers[0];
                    const name = offerFirst.name;
                    const isAvailable = !namesAllProducts.includes(name);

                    applyStocksOfferPropsAndCollectAxes(offers);

                    // Оффери з ненульовим чистим залишком (включно з продажем у мінус)
                    const nonZeroOffers = offers.filter(offer => {
                        const quantity = offer.quantity - (offer.in_reserve || 0);
                        return quantity !== 0;
                    });

                    if (nonZeroOffers.length === 0) return null;

                    /* Вкладки по товару (product_id): будь-який проданий варіант у IG → у «Основних» уся сітка SKU */
                    const linePid = Number(offerFirst.product_id);
                    const hasIgSalesForProduct =
                        Number.isFinite(linePid) && igSoldProductSet.has(linePid);
                    if (igFilterActive && Number.isFinite(linePid)) {
                        if (effectiveStockTab === "main" && !hasIgSalesForProduct) return null;
                        if (effectiveStockTab === "hidden" && hasIgSalesForProduct) return null;
                    }

                    nonZeroOffers.sort((a, b) => {
                        const len = Math.max(a.props.length, b.props.length);
                        for (let i = 0; i < len; i++) {
                            const c = String(a.props[i] || "").localeCompare(String(b.props[i] || ""), "uk");
                            if (c) return c;
                        }
                        return 0;
                    });

                    const { colors: colorSet, sizes: sizeSet, models: modelSet } =
                        applyStocksOfferPropsAndCollectAxes(nonZeroOffers);

                    const colors = Array.from(colorSet).sort(sorEndStart);
                    const sizes = Array.from(sizeSet).sort(sorStartEnd);
                    const models = Array.from(modelSet).sort(sorEndStart);

                    const { rows: visibleRows, visibleSizes, displayTotal, negativeSum } = buildVisibleStockTableRows(
                        models,
                        colors,
                        sizes,
                        nonZeroOffers
                    );

                    if (
                        process.env.NODE_ENV === "development" &&
                        nonZeroOffers.length > 0 &&
                        visibleRows.length === 0
                    ) {
                        console.warn(
                            "[stocks-ui] Є не нульовий залишок, але сітка порожня (перевірте назви властивостей у CRM):",
                            { name, product_id: offerFirst.product_id, sample: nonZeroOffers[0]?.properties }
                        );
                    }

                    if (isAvailable && visibleRows.length > 0) {
                        const sewBtn = canOpenSewingStatusModal
                            ? productSewingStatusButtonMeta(
                                  offerFirst.product_id,
                                  products,
                                  excludedSkuSet,
                                  sewingExclusionsStatus
                              )
                            : null;
                        return (
                            <Accordion.Item key={p} eventKey={p}>
                                <Accordion.Header>
                                    <div className="d-flex align-items-center justify-content-between w-100 gap-2 me-2 flex-wrap">
                                        <div className="d-flex align-items-center gap-2 flex-grow-1 min-w-0">
                                            <span className="stocks-acc-header-title text-truncate">
                                                {name}
                                            </span>
                                            <span className="stocks-acc-header-counts flex-shrink-0">
                                                <span className="stocks-acc-header-plus">
                                                    ({displayTotal})
                                                </span>
                                                {negativeSum < 0 ? (
                                                    <span className="stocks-acc-header-minus">
                                                        {negativeSum}
                                                    </span>
                                                ) : null}
                                            </span>
                                        </div>
                                        {sewBtn ? (
                                            <Button
                                                type="button"
                                                size="sm"
                                                variant={sewBtn.variant}
                                                disabled={sewBtn.disabled}
                                                className="flex-shrink-0 text-nowrap"
                                                title={sewBtn.title}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSewingModal({
                                                        open: true,
                                                        productId: offerFirst.product_id,
                                                        name,
                                                    });
                                                }}
                                            >
                                                {sewBtn.label}
                                            </Button>
                                        ) : null}
                                        {isAD ? (
                                            <>
                                                <Button
                                                    type="button"
                                                    size="sm"
                                                    variant="outline-dark"
                                                    className="flex-shrink-0 text-nowrap"
                                                    title="Картка товару / друк"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setProductCardModal({
                                                            open: true,
                                                            mode: "view",
                                                            productId: offerFirst.product_id,
                                                            name,
                                                        });
                                                    }}
                                                >
                                                    Картка
                                                </Button>
                                                <Button
                                                    type="button"
                                                    size="sm"
                                                    variant="outline-dark"
                                                    className="flex-shrink-0 text-nowrap"
                                                    title="Редагувати картку товару"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setProductCardModal({
                                                            open: true,
                                                            mode: "edit",
                                                            productId: offerFirst.product_id,
                                                            name,
                                                        });
                                                    }}
                                                >
                                                    Редагувати
                                                </Button>
                                            </>
                                        ) : null}
                                    </div>
                                </Accordion.Header>
                                <Accordion.Body>
                                    <table className="table table-bordered">
                                        <thead>
                                        <tr>
                                            {models.length ? (
                                                <>
                                                    <th style={{ textAlign: "center" }}>Тип</th>
                                                    <th>Колір</th>
                                                    {visibleSizes.map((s, i) => (
                                                        <th key={i} style={{ textAlign: "center" }}>{s}</th>
                                                    ))}
                                                </>
                                            ) : (
                                                <>
                                                    <th>Колір</th>
                                                    {visibleSizes.map((s, i) => (
                                                        <th key={i} style={{ textAlign: "center" }}>{s}</th>
                                                    ))}
                                                </>
                                            )}
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {renderFilteredStockRows(visibleRows, visibleSizes, nonZeroOffers, models.length > 0)}
                                        </tbody>
                                    </table>
                                </Accordion.Body>
                            </Accordion.Item>
                        );
                    }
                })
                .filter(Boolean) // null: нет офферов с ненулевым чистым остатком или нет строк таблицы
        ) : (
            <div style={{ textAlign: "center" }}>
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </div>
        );
    };


    const lastUpdateInfo = describeStockLastUpdate(lastUpdate);

    return (
            <div className="stocks-page">
                <header style={{ padding: "1rem 0" }}>
                    <div className="container">
                        <div className="stocks-page__masthead">
                            <h2 className="stocks-page__title">Склад</h2>
                            <div className="stocks-page__actions">
                                {canSeeBags ? (
                                    <Button
                                        className="stocks-page__action-btn"
                                        variant={showBags ? "outline-secondary" : "dark"}
                                        onClick={() => setShowBags((v) => !v)}
                                    >
                                        {showBags ? "Сховати мішки" : "Мішки"}
                                    </Button>
                                ) : null}
                                {isAD ? (
                                    <Button
                                        className="stocks-page__action-btn"
                                        variant={showProblems ? "outline-secondary" : "danger"}
                                        onClick={handleToggleProblemProducts}
                                    >
                                        {showProblems ? "Сховати проблемні товари" : "Проблемні товари"}
                                    </Button>
                                ) : null}
                                {isAD ? (
                                    <Button className="stocks-page__action-btn" variant="dark" onClick={sendMessage}>
                                        Відправити мінуси
                                    </Button>
                                ) : null}
                            </div>
                        </div>
                        <InputGroup>
                            <div className="w-100">
                                {lastUpdateInfo ? (
                                    <p className={`stocks-page__last-update${lastUpdateInfo.stale ? " stocks-page__last-update--stale" : ""}`}>
                                        📅 Останнє оновлення складу: {lastUpdateInfo.text}
                                        {lastUpdateInfo.stale ? " — ⚠️ схоже, автооновлення не працює" : ""}
                                    </p>
                                ) : null}
                                <div className="stocks-page__toolbar">
                                    <Form.Select
                                        id="warehouseFilter"
                                        className="stocks-page__warehouse-select"
                                        value={selectedWarehouseId}
                                        onChange={handleSelectChange}
                                        aria-label="Оберіть склад"
                                    >
                                        {SOURCE_MAP.map(warehouse => (
                                            <option key={warehouse.id} value={warehouse.id}>
                                                {warehouse.name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                    <span className="stocks-page__total">{totalStock} шт.</span>
                                    {warehouseNegativeSum < 0 ? (
                                        <span className="stocks-page__total-negative">
                                            {warehouseNegativeSum} шт.
                                        </span>
                                    ) : null}
                                </div>
                            </div>
                        </InputGroup>
                    </div>
                </header>
                <main style={{ padding: "1rem 0" }}>
                    <div className="container">
                        {canSeeBags && showBags ? <StockBagsPanel products={products} /> : null}
                        {showProblems && (
                            <div className="stocks-page__problems-panel mt-4">
                                <h4 className="stocks-page__problems-title">
                                    🚨 Проблемні товари ({problemProducts.length})
                                </h4>

                                {problemsFetchError ? (
                                    <div className="alert alert-danger mb-0" role="alert">
                                        {problemsFetchError}
                                    </div>
                                ) : problemProducts.length === 0 ? (
                                    <p>Все заповнено 👍</p>
                                ) : (
                                    <div className="stocks-page__table-scroll">
                                        <table className="table table-bordered stocks-page__problem-table">
                                            <thead>
                                            <tr>
                                                <th>Назва</th>
                                                <th>Кількість</th>
                                                <th>ID</th>
                                                <th>SKU</th>
                                                <th>Закупка</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {problemProducts.map((p, i) => (
                                                <tr key={i}>
                                                    <td>{p.product.name}</td>
                                                    <td>{p.quantity}</td>
                                                    <td>{p.product_id}</td>
                                                    <td style={{ color: !p.sku ? "red" : "black" }}>
                                                        {p.sku || "—"}
                                                    </td>
                                                    <td style={{ color: !p.purchased_price ? "red" : "black" }}>
                                                        {p.purchased_price || "—"}
                                                    </td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        )}
                        {showManagerStockTabs ? (
                            <>
                                <Nav
                                    variant="tabs"
                                    className="mb-3 stocks-page__stock-tabs"
                                    activeKey={stockTab}
                                    onSelect={(k) => {
                                        if (k) setStockTab(k);
                                    }}
                                >
                                    <Nav.Item>
                                        <Nav.Link eventKey="main">Основні</Nav.Link>
                                    </Nav.Item>
                                    <Nav.Item>
                                        <Nav.Link eventKey="hidden">Сховані</Nav.Link>
                                    </Nav.Item>
                                    <Nav.Item>
                                        <Nav.Link eventKey="all">Всі</Nav.Link>
                                    </Nav.Item>
                                </Nav>
                                {igSalesError ? (
                                    <p className="text-muted small mb-2">
                                        Не вдалося завантажити продажі Instagram; вкладки «Основні» / «Сховані»
                                        тимчасово як «Всі».
                                    </p>
                                ) : null}
                            </>
                        ) : null}
                        <Accordion defaultActiveKey={'0'} className="lava-accordion">
                            {renderAccordionItems()}
                        </Accordion>
                    </div>
                </main>
                <section>
                    <div className="container">
                        <div className="inner-header">
                            <div>
                                <Button onClick={handleRefresh} disabled={loading} variant="dark">
                                    {loading ? "Оновлення…" : "Оновити склади"}
                                </Button>
                            </div>
                            <ProgressBar />
                        </div>
                    </div>
                </section>

                {canOpenSewingStatusModal ? (
                    <ProductSewingStatusModal
                        show={sewingModal.open}
                        onHide={() =>
                            setSewingModal({ open: false, productId: null, name: "" })
                        }
                        productId={sewingModal.productId}
                        productName={sewingModal.name}
                        allProducts={products}
                        excludedSkuSet={excludedSkuSet}
                        onApplied={async () => {
                            await loadSewingExclusions();
                        }}
                    />
                ) : null}

                {isAD ? (
                    <ProductCardModal
                        show={productCardModal.open}
                        mode={productCardModal.mode}
                        onHide={() =>
                            setProductCardModal({ open: false, mode: "view", productId: null, name: "" })
                        }
                        productId={productCardModal.productId}
                        productName={productCardModal.name}
                    />
                ) : null}
            </div>
        )

    }